import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { enumDocumentRelatedEntityType, enumDocumentState } from '@data-layer/enums/documents';
import { ACCESS_TOKEN, TOKEN_TYPE } from '@constants/setting-keys.const';

@Injectable({
  providedIn: 'root',
})
export class DocumentsRepository {
  constructor(private apiService: ApiService) {}

  public create(companyId: number, document: DocumentCreateModel, isShowLoader: boolean = true): Observable<DocumentModel> {
    return this.apiService.post<DocumentModel>(`${environment.url.prefixCustoms}v2/companies/${companyId}/documents`, document, null, {
      isShowLoader,
    });
  }

  public copy(documentId: number, document: DocumentCreateModel, isShowLoader: boolean): Observable<never> {
    return this.apiService.post(`${environment.url.prefixCustoms}v2/documents/${documentId}`, document, null, {
      isShowLoader,
    });
  }

  public uploadFile(documentId: number, file: File, isShowLoader: boolean = true): Observable<never> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.apiService.post(
      `${environment.url.prefixCustoms}v2/documents/${documentId}/data`,
      formData,
      {
        headers: {
          Authorization: `${localStorage.getItem(TOKEN_TYPE)} ${localStorage.getItem(ACCESS_TOKEN)}`,
        },
      },
      { isShowLoader },
      (err) => throwError(err),
    );
  }

  public uploadFileByUrl(uploadUrl: string, file: File, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.put(
      uploadUrl,
      file,
      {
        headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/x-www-form-urlencoded' },
      },
      { isShowLoader },
    );
  }

  public uploadCustomsImportFile(companyId: number, file: File, isShowLoader: boolean = true): Observable<never> {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    return this.apiService.post(
      `${environment.url.prefixCustoms}v2/import-customs-data/${companyId}`,
      formData,
      {
        headers: {
          // this is used to set Content-Type
          Authorization: `${localStorage.getItem(TOKEN_TYPE)} ${localStorage.getItem(ACCESS_TOKEN)}`,
        },
      },
      { isShowLoader },
    );
  }

  public delete(id: number, isShowLoader: boolean = true): Observable<never> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/documents/${id}`, null, { isShowLoader });
  }

  public getDownloadLink(id: number, isShowLoader: boolean = true): Observable<{ downloadUrl: string }> {
    return this.apiService.get<{ downloadUrl: string }>(`${environment.url.prefixCustoms}v2/documents/${id}/data-url`, null, {
      isShowLoader,
    });
  }

  public getBillDocuments(billId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.prefixCustoms}v2/bills/${billId}/documents`, null, {
      isShowLoader,
    });
  }

  public getESDocuments(entrySummaryId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.prefixCustoms}v2/entry-summaries/${entrySummaryId}/documents`, null, {
      isShowLoader,
    });
  }

  public getIsfDocuments(isfId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.prefixCustoms}v2/isfs/${isfId}/documents`, null, { isShowLoader });
  }

  public getCompanyDocuments(companyId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.companies}${companyId}/documents`, null, { isShowLoader });
  }

  public getLCLDocuments(shipmentId: number, isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.prefixFreight}shipments/${shipmentId}/documents`, null, {
      isShowLoader,
    });
  }

  public getDocumentState(documentId: number, isShowLoader: boolean): Observable<enumDocumentState> {
    return this.apiService.get<enumDocumentState>(`${environment.url.prefixCustoms}v2/documents/${documentId}/state`, null, {
      isShowLoader,
    });
  }

  public getArchive(referenceNumber: string, documentsIds: number[]): Observable<Blob> {
    return this.apiService.post(
      `${environment.url.prefixCustoms}v2/documents/zip-archive`,
      { referenceNumber, documentsIds },
      {
        responseType: 'blob',
      },
    );
  }

  public downloadBlobFile(blob: Blob, filename: string): void {
    if ((navigator as any).msSaveBlob) {
      // IE 10+
      (navigator as any).msSaveBlob(blob, filename);
    } else {
      const link: HTMLAnchorElement = document.createElement('a');
      // Browsers that support HTML5 download attribute
      if (link.download !== undefined) {
        const url: string = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  public getBatchDocuments(entityType: enumDocumentRelatedEntityType, ids: number[]): Observable<DocumentModel[]> {
    return this.apiService.get<DocumentModel[]>(`${environment.url.prefixCustoms}v2/documents`, { params: { entityType, ids } });
  }

  public ocrProcessing(documentId: number): Observable<void> {
    return this.apiService.post<void>(`${environment.url.prefixCustoms}v2/documents/${documentId}/ocr`);
  }

  private getFileName(contentDisposition: string): string {
    return (
      contentDisposition
        ?.split(';')?.[1]
        ?.split('filename')?.[1]
        ?.split('=')?.[1]
        ?.trim()
        ?.match(/"([^"]+)"/)?.[1] || new Date().toString()
    );
  }
}
